import { AUTH_ENDPOINTS, FETCH_OPTIONS, OPERATORI_ENDPOINTS, SOCIETA_ENDPOINTS } from '../../config/constants';

const parseApiError = async (res: Response, fallback: string): Promise<string> => {
  const payload = await res.json().catch(() => null);

  if (!payload) {
    return fallback;
  }

  if (typeof payload.detail === 'string' && payload.detail.trim()) {
    return payload.detail;
  }

  if (Array.isArray(payload.detail) && payload.detail.length > 0) {
    const details = payload.detail
      .map((item: any) => item?.msg)
      .filter((msg: unknown) => typeof msg === 'string' && msg.trim().length > 0);
    if (details.length > 0) {
      return details.join(', ');
    }
  }

  if (typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  if (typeof payload.error === 'string' && payload.error.trim()) {
    return payload.error;
  }

  return fallback;
};

/**
 * Request per registrare un nuovo utente
 */
export interface RegisterUserRequest {
  email: string;
  password: string;
  role: 'admin' | 'user' | 'root';
}

/**
 * Request per aggiungere un operatore
 */
export interface AddOperatoreRequest {
  nome: string;
  cognome: string;
  email: string;
  tariffa: number;
  id_societa: number;
}

export interface OperatoreResponse {
  id: number;
  nome: string;
  cognome: string;
  email: string;
  tariffa: number;
  id_societa: number;
}

export interface UserResponse {
  id: number;
  email: string;
  role: 'admin' | 'user' | 'root';
  first_login: boolean;
}

export interface SocietaResponse {
  id: number;
  societa: string;
  email: string;
}

/**
 * Registra un nuovo utente nel sistema
 */
export const registerUser = async (data: RegisterUserRequest): Promise<void> => {
  const res = await fetch(AUTH_ENDPOINTS.REGISTER_USER, {
    ...FETCH_OPTIONS,
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    throw new Error(await parseApiError(res, 'Errore nella registrazione utente'));
  }
};

/**
 * Aggiunge un operatore al sistema
 */
export const addOperatore = async (data: AddOperatoreRequest): Promise<void> => {
  const res = await fetch(OPERATORI_ENDPOINTS.ADD_OPERATORE, {
    ...FETCH_OPTIONS,
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    throw new Error(await parseApiError(res, 'Errore nell\'aggiunta operatore'));
  }
};

export const fetchOperatori = async (): Promise<OperatoreResponse[]> => {
  const res = await fetch(OPERATORI_ENDPOINTS.ADD_OPERATORE, {
    ...FETCH_OPTIONS,
    method: 'GET',
  });

  if (!res.ok) {
    throw new Error(await parseApiError(res, 'Errore nel caricamento operatori'));
  }

  const data = await res.json();
  return Array.isArray(data) ? data : [];
};

export const fetchUsers = async (): Promise<UserResponse[]> => {
  const res = await fetch('/api/services/auth/users', {
    ...FETCH_OPTIONS,
    method: 'GET',
  });

  if (!res.ok) {
    throw new Error(await parseApiError(res, 'Errore nel caricamento utenti'));
  }

  const data = await res.json();
  return Array.isArray(data) ? data : [];
};

export const fetchSocieta = async (): Promise<SocietaResponse[]> => {
  const res = await fetch(SOCIETA_ENDPOINTS.GET_SOCIETA, {
    ...FETCH_OPTIONS,
    method: 'GET',
  });

  if (!res.ok) {
    throw new Error(await parseApiError(res, 'Errore nel caricamento societa'));
  }

  const data = await res.json();
  return Array.isArray(data) ? data : [];
};

export const deleteUser = async (email: string): Promise<void> => {
  const res = await fetch(`/api/services/auth/users/${encodeURIComponent(email)}`, {
    ...FETCH_OPTIONS,
    method: 'DELETE',
  });

  if (!res.ok) {
    throw new Error(await parseApiError(res, 'Errore nella cancellazione utente'));
  }
};

/**
 * Registra utente e aggiunge operatore in sequenza
 * Prima crea l'utente, poi se va a buon fine crea l'operatore
 */
export const createOperatore = async (
  nome: string,
  cognome: string,
  email: string,
  tariffa: number,
  id_societa: number,
  role: 'admin' | 'user' | 'root',
  password: string = 'Timesheet2025' // Password di default
): Promise<void> => {
  // Step 1: Registra l'utente
  await registerUser({
    email,
    password,
    role,
  });

  // Step 2: Aggiungi l'operatore
  try {
    await addOperatore({
      nome,
      cognome,
      email,
      tariffa,
      id_societa,
    });
  } catch (error) {
    // Compensazione: se l'inserimento operatore fallisce, elimina l'utente appena creato.
    try {
      await deleteUser(email);
    } catch {
      // Se fallisce anche la compensazione, propaghiamo comunque l'errore originale.
    }
    throw error;
  }
};
